import { useRef } from 'react';
import { API } from '../../utils/amplifyWrapper';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { uploadToS3 } from './useClonedVoice';
import { assertFeatureCreditsOk } from '../organisation/featureCreditsGuard';

export interface IVoiceIsolation {
    id: string;
    integration_id: string;
    name: string;
    source_audio_path: string;
    source_format?: string;
    audio_duration?: number;
    status: 'processing' | 'ready' | 'failed';
    use_tta?: boolean;
    output_format?: string;
    vocals_path?: string;
    background_path?: string;
    job_id?: string;
    error?: string;
    created_at?: string;
    updated_at?: string;
}

// ── List ─────────────────────────────────────────────────────────────────────
export const fetchVoiceIsolations = async (org_id: string, integration_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_isolation`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as IVoiceIsolation[];
};

export const useVoiceIsolations = ({ org_id, integration_id }: { org_id: string; integration_id: string }) => {
    const queryClient = useQueryClient();
    // See useVoiceChanges: detect a job settling to refresh the credit balance (charge-on-completion).
    const prevActiveRef = useRef<number | null>(null);
    return useQuery(
        ['voice_isolations', org_id, integration_id],
        () => fetchVoiceIsolations(org_id, integration_id),
        {
            enabled: !!(org_id && integration_id),
            // Poll while any job is still processing so the history flips to ready/failed on its own.
            refetchInterval: (data) =>
                data && data.some((i) => i.status === 'processing') ? 5000 : false,
            onSuccess: (data) => {
                const active = data.filter((i) => i.status === 'processing').length;
                if (prevActiveRef.current !== null && active < prevActiveRef.current) {
                    queryClient.invalidateQueries(['organisation_usage', org_id]);
                }
                prevActiveRef.current = active;
            },
        }
    );
};

export const fetchVoiceIsolationById = async (org_id: string, integration_id: string, isolation_id: string) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_isolation/${isolation_id}`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as IVoiceIsolation;
};

// ── Presigned upload (audio-only, lands in the public bucket) ──────────────────
export const fetchIsolationPresignedUrl = async ({
    org_id,
    integration_id,
    fileName,
    contentType,
}: {
    org_id: string;
    integration_id: string;
    fileName: string;
    contentType: string;
}) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/audio/signed-url?file_name=${encodeURIComponent(fileName)}&content_type=${encodeURIComponent(contentType)}&file_name_prefix=voice-isolation&file_type=voice-isolation`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as { signed_url: string; url: string };
};

// ── Create ───────────────────────────────────────────────────────────────────
interface ICreateVoiceIsolationParams {
    org_id: string;
    integration_id: string;
    name: string;
    source_audio_path: string;
    source_format: string;
    audio_duration?: number;
    use_tta?: boolean;
}

export const useCreateVoiceIsolation = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({
            org_id,
            integration_id,
            name,
            source_audio_path,
            source_format,
            audio_duration,
            use_tta,
        }: ICreateVoiceIsolationParams) => {
            const response = await API.post(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_isolation`,
                {
                    body: { name, source_audio_path, source_format, audio_duration, use_tta },
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            assertFeatureCreditsOk(response);
            return { isolationId: response.data.id as string, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_isolations', org_id, integration_id]);
            },
        }
    );
};

// ── Update / Delete ────────────────────────────────────────────────────────────
export const useUpdateVoiceIsolation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({
            org_id,
            integration_id,
            isolation_id,
            name,
            status,
        }: {
            org_id: string;
            integration_id: string;
            isolation_id: string;
            name?: string;
            status?: 'processing' | 'ready' | 'failed';
        }) => {
            const response = await API.put(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_isolation/${isolation_id}`,
                { body: { name, status }, headers: { 'Content-Type': 'application/json' } }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_isolations', org_id, integration_id]);
            },
        }
    );
};

export const useDeleteVoiceIsolation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ org_id, integration_id, isolation_id }: { org_id: string; integration_id: string; isolation_id: string }) => {
            const response = await API.del(
                'main',
                `/organisation/${org_id}/integration/${integration_id}/voice_isolation/${isolation_id}`,
                { headers: { 'Content-Type': 'application/json' } }
            );
            return { response, org_id, integration_id };
        },
        {
            onSuccess: ({ org_id, integration_id }) => {
                queryClient.invalidateQueries(['voice_isolations', org_id, integration_id]);
            },
        }
    );
};

// ── Download a finished track (vocals | background) → signed URL ───────────────
export const fetchIsolationDownloadUrl = async (
    org_id: string,
    integration_id: string,
    isolation_id: string,
    track: 'vocals' | 'background' = 'vocals'
) => {
    const response = await API.get(
        'main',
        `/organisation/${org_id}/integration/${integration_id}/voice_isolation/${isolation_id}/download?track=${track}`,
        { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data as { url: string; track: string };
};

// Re-exported for convenience so the page can upload without importing from useClonedVoice.
export { uploadToS3 };
